import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type UserRole = 'owner' | 'admin' | 'agent' | 'viewer'

export type AuthState = {
  token: string | null
  tenantId: string | null
  tenant: {
    id: string
    slug: string
    name: string
    businessType?: string | null
    onboarded?: boolean
  } | null
  user: { 
    id: string
    email: string
    name?: string
    role?: UserRole
  } | null
  login: (payload: { token: string; tenantId?: string | null; user?: AuthState['user']; tenant?: AuthState['tenant'] }) => void
  logout: () => void
}

const authResetListeners = new Set<() => void>()

/**
 * Registra un callback que se ejecuta al cerrar sesión o al cambiar de tenant.
 * Útil para purgar cachés en capas superiores (e.g. React Query) sin acoplar Zustand a TanStack Query.
 */
export function onAuthReset(callback: () => void): () => void {
  authResetListeners.add(callback)
  return () => authResetListeners.delete(callback)
}

function notifyAuthReset(): void {
  authResetListeners.forEach((fn) => {
    try {
      fn()
    } catch {
      // Ignorar errores en callbacks desacoplados
    }
  })
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      tenantId: null,
      tenant: null,
      user: null,
      login: ({ token, tenantId, user, tenant }) => set((state) => {
        if (tenantId !== undefined && tenantId !== state.tenantId) {
          notifyAuthReset()
        }
        return { 
          token, 
          tenantId: tenantId !== undefined ? tenantId : state.tenantId, 
          user: user !== undefined ? user : state.user,
          tenant: tenant !== undefined ? tenant : state.tenant
        }
      }),
      logout: () => {
        // Clear refresh token from localStorage
        if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
          window.localStorage.removeItem('refreshToken')
        }
        notifyAuthReset()
        // Reset all state
        set({ token: null, tenantId: null, tenant: null, user: null })
      },
    }),
    {
      name: 'wa-saas-auth',
      partialize: (state) => ({ 
        token: state.token, 
        tenantId: state.tenantId, 
        tenant: state.tenant,
        user: state.user 
      }),
    }
  )
)
